import { ArcLayer, TextLayer } from "@deck.gl/layers";
import { useEffect, useState, useMemo, useCallback } from "react";
import DeckGL from "@deck.gl/react";
import {
    _GlobeView as GlobeView,
    LightingEffect,
    _SunLight as SunLight,
    AmbientLight,
    GlobeViewState,
    FlyToInterpolator,
} from "@deck.gl/core";
import { GeoJsonLayer } from "@deck.gl/layers";
import { SimpleMeshLayer } from "@deck.gl/mesh-layers";
import { ScenegraphLayer } from "@deck.gl/mesh-layers";
import { CubeGeometry, SphereGeometry } from "@luma.gl/engine";
import { PositionAndVelocity, propagate, SatRec, twoline2satrec } from "satellite.js";
import { sgp4ToLonLatAlt, toDeg } from "./common/Orbital";
import { MemoSatelliteEntry } from "./common/SatelliteEntry";
import * as SunCalc from "suncalc";
import { SimMode, Toolbar } from "./common/OrbitToolbar";
import { useLoaderData } from "react-router-dom";
import { SatelliteInfo, TleRec } from "./api/Application";
import { OrderedMap } from "js-sdsl";
import { LngLat, LngLatAlt } from "./common/Geography";

const SWATH_KM      = 12;
const HALF_SWATH_KM = SWATH_KM / 2;
const KM_PER_DEG    = 111.32;

interface Sat  {
    id:          number;
    name:        string;
    rec:         SatRec;
    pv:          PositionAndVelocity;
    position:    [number, number, number];
    orientation: [number, number, number];
    epoch:       Date
}

interface Swath {
    from: [number, number, number];
    to:   [number, number, number];
}

function groundPoint( rec: SatRec, date: Date, orientation: [number, number, number] ): [number, number] {
    const pos = sgp4ToLonLatAlt( rec, date );   // [lon, lat, altKm]
    const altKm = pos[2];

    const heading = -orientation[1] * Math.PI / 180;
    const tiltDeg = orientation[0] - getOrientation( rec, date )[0];  // cross-track tilt only
    const offsetKm = altKm * Math.tan( tiltDeg * Math.PI / 180 );

    const crossBr = heading + Math.PI / 2;
    const cosLat = Math.cos( pos[1] * Math.PI / 180 );
    const dLat = ( offsetKm / KM_PER_DEG ) * Math.cos( crossBr );
    const dLon = ( offsetKm / KM_PER_DEG ) * Math.sin( crossBr ) / cosLat;
    return [pos[0] + dLon, pos[1] + dLat];
}

const cameraLight = new AmbientLight( {
    id:        "ambient",
    color:     [255, 255, 255],
    intensity: 2.3
} );

function getSign( mode: SimMode ): number {
    if( mode === SimMode.Pause  ) { return  0; }
    if( mode === SimMode.Play   ) { return +1; }
    if( mode === SimMode.Rewind ) { return -1; }
    throw new Error( "Err!" );
}

function epochMsFromSatRec( satRec: SatRec ): number {
    return ( satRec.jdsatepoch - 2440587.5 ) * 86400000;
}

function getOrientationToTarget( rec: SatRec, date: Date, target: LngLat ): LngLatAlt {
    const base = getOrientation( rec, date );
    const pos  = sgp4ToLonLatAlt( rec, date );

    const cosLat = Math.cos( pos[1] * Math.PI / 180 );
    const dEast  = ( target[0] - pos[0] ) * cosLat;
    const dNorth = ( target[1] - pos[1] );

    const headingRad    = -base[1] * Math.PI / 180;
    const vEast         = Math.sin( headingRad );
    const vNorth        = Math.cos( headingRad );
    const crossTrackDeg = dEast * vNorth - dNorth * vEast;
    const crossTrackKm  = crossTrackDeg * 111.32;
    const tiltDeg       = Math.atan2( crossTrackKm, pos[2] ) * 180 / Math.PI;
    return [base[0] + tiltDeg, base[1], base[2]] as LngLatAlt;
}

// const TRAIL_SECONDS = -5;
// interface SwathRibbon {
//     polygon: [number, number][];
// }

// function swathEdges( sat: Sat, t: Date, target: number[] | undefined ): { left: [number, number]; right: [number, number] } | null {
//     const ori = target
//         ? getOrientationToTarget( sat.rec, t, target as LngLat )
//         : getOrientation( sat.rec, t );
//     const tiltDeg = ori[0] - getOrientation( sat.rec, t )[0];
//     if( Math.abs( tiltDeg ) > 30.0 ) return null;

//     const center  = groundPoint( sat.rec, t, ori );
//     const heading = -ori[1] * Math.PI / 180;
//     const crossBr = heading + Math.PI / 2;
//     const cosLat  = Math.cos( center[1] * Math.PI / 180 );
//     const dLat = ( HALF_SWATH_KM / KM_PER_DEG ) * Math.cos( crossBr );
//     const dLon = ( HALF_SWATH_KM / KM_PER_DEG ) * Math.sin( crossBr ) / cosLat;
//     return {
//         left: [center[0] - dLon, center[1] - dLat],
//         right: [center[0] + dLon, center[1] + dLat],
//     };
// }

function getOrientation( rec: SatRec, date: Date ): [number, number, number] {
    const delta = 1000; // 1 second delta
    const p0    = sgp4ToLonLatAlt( rec, date );
    const p1    = sgp4ToLonLatAlt( rec, new Date( date.getTime() + delta ) );

    // calculate heading (yaw) from longitude/latitude change
    const dLon = p1[0] - p0[0];
    const dLat = p1[1] - p0[1];

    // handle wraparound at ±180°
    const adjDLon = dLon > 180 ? dLon - 360 : dLon < -180 ? dLon + 360 : dLon;

    // calculate heading angle (0 deg = north, 90 deg = east)
    // atan2(dlon, dlat) gives angle from north
    const headingRad = Math.atan2( adjDLon * Math.cos( p0[1] * Math.PI / 180 ), dLat );
    const headingDeg = headingRad * 180 / Math.PI;

    // calculate pitch from altitude change relative to horizontal distance
    const horizDist = Math.sqrt(
        ( adjDLon * Math.cos( p0[1] * Math.PI / 180 ) * 111.32 ) ** 2 +
        ( dLat * 110.574 ) ** 2
    ); // km
    const dAlt     = p1[2] - p0[2]; // km
    const pitchRad = Math.atan2( dAlt, horizDist );
    const pitchDeg = pitchRad * 180 / Math.PI;

    // return [pitch, yaw, roll] - adjust yaw by 90 deg to align model"s forward axis
    return [pitchDeg + 0, -headingDeg, 90];
}

export function OrbitDemo2() {
    const perfStart = performance.now();
    const [ satInfos, tleRecs   ] = useLoaderData() as [SatelliteInfo[], TleRec[]];    
    const [ mode,     setMode   ] = useState<SimMode>( SimMode.Play );
    const [ anchor,   setAnchor ] = useState<number>( Date.now() ); //the date we want to view
    const [ start,    setStart  ] = useState<number>( Date.now() ); //when we start measuring sim-time
    const [ rate,     setRate   ] = useState( 1 ); //maps real-time changes to sim-time
    const [ now,      setNow    ] = useState( new Date() );
    const time = now.getTime();

    //targeting and viewstate
    const [ target,     setTarget    ] = useState<number[]>();
    const [ viewState,  setViewState ] = useState<GlobeViewState>( {         
        longitude: -95.37,
        latitude:   29.76,
        zoom:        2.00,
        transitionDuration: 900,
        transitionInterpolator: new FlyToInterpolator( { screenSpeed: 60, speed: 0.01, maxDuration: 3.0 })
    } );

    const satNameMap = useMemo( () => {
        const map = new Map<number, string>();        
        for( const satInfo of satInfos ) {
            map.set( satInfo.noradCatId, satInfo.name );
        }
        return map;
    }, [] );

    const tleMap = useMemo( () => {
        const bySat = new Map<number, OrderedMap<number, SatRec>>();
        for( const rec of tleRecs ) {
            const id     = rec.noradCatId;
            const satRec = twoline2satrec( rec.line1, rec.line2 );
            let map = bySat.get( id );
            if( !map ) {
                map = new OrderedMap<number, SatRec>( [], ( a, b ) => a - b );
                bySat.set( id, map );
            }
            map.setElement( epochMsFromSatRec( satRec ), satRec );
        }
        return bySat;
    }, [tleRecs] );

    const getTle = useCallback( ( satId: number, t: number ): SatRec => {
        const map = tleMap.get( satId )!;
        const itr = map.reverseUpperBound( t );
        //if t is before all epochs
        if( itr.equals( map.rEnd() ) ) {
            //fall back to earliest tle
            return map.front()?.[1]!;
        }
        return itr.pointer[1];
    }, [tleMap] );

    //satellite setup
    const sats = useMemo( () => {
        const satIds = Array.from( tleMap.keys() );
        return satIds.flatMap( id => {
            const rec  = getTle( id, time );
            const name = satNameMap.get( id ) ?? `Unknown Id: ${id}`;
            const pv   = propagate( rec, now );
            if( !pv ) { 
                console.error( "No propagate! Investigate!" );
                return [];
            }
            const position    = sgp4ToLonLatAlt( rec, now );
            const orientation = target ? getOrientationToTarget( rec, now, target as LngLat ) : getOrientation( rec, now );
            const epoch       = new Date( epochMsFromSatRec( rec ) );
            const res: Sat[] = [{ id, name, rec, pv, position, orientation, epoch }];
            return res;
        } );
    }, [tleMap, tleRecs, now] );

    //animation
    useEffect( () => {
        let frameId: number;
        const animate = () => {
            frameId = requestAnimationFrame( animate );
            //time computation (cheap and fast)
            const delt = Date.now() - start;
            const incr = ( delt ) * rate;
            const sign = getSign( mode );
            const res  = anchor + ( incr * sign );
            setNow( new Date( res ) );
        };
        animate();
        return () => cancelAnimationFrame( frameId );
    }, [mode, rate, anchor, start] );

    const sunLight = useMemo( () => {
        return new SunLight( {
            id:        "sun",
            timestamp: now,
            color:     [255, 255, 255],
            intensity: 3.0,
            _shadow:   false,
        } );
    }, [now] );

    const lights    = new LightingEffect( { sunLight, cameraLight } );
    const globeView = useMemo( () => new GlobeView( { resolution: 1, parameters: { cullMode: "none" } } ), [] );
    const cubeMesh  = useMemo( () => new CubeGeometry(), [] );        
    const bgLayers  = useMemo( () => [
        new SimpleMeshLayer( {
            id: "water",
            data: [0],
            mesh: new SphereGeometry( { radius: 6.37e6, nlat: 64, nlong: 128 } ),
            coordinateSystem: "cartesian",
            getPosition: [0,  0,  0],
            getColor:    [5, 10, 25],
            material: {
                ambient:    0.5,
                diffuse:    0.5,
                shininess: 50.0,
                specularColor: [32, 32, 32]
            },
        } ),
        new GeoJsonLayer( {
            id: "land",
            data: "https://d2ad6b4ur7yvpq.cloudfront.net/naturalearth-3.3.0/ne_50m_land.geojson",
            extruded: true,
            getFillColor: [60, 100, 50],
            material: {
                ambient:     0.25,
                diffuse:     1.00,
                shininess: 100.00,
                specularColor: [32, 32, 32] //matte
            }
        } )
    ], [] );

    const orbitScale      = 1000;
    const orbitTextScale  = 1000;
    const orbitTextOffset =  300; 
    interface ArcData {
        to:   [number, number, number];
        from: [number, number, number];
    }

    const swaths: Swath[] = useMemo( () => sats.flatMap( sat => {
        const tiltDeg = sat.orientation[0] - getOrientation( sat.rec, now )[0];
        if( Math.abs( tiltDeg ) > 30.0 ) return [];
        const heading     = -sat.orientation[1];              // undo the negation in getOrientation
        const crossTrack  = ( heading + 90 ) * Math.PI / 180; // perpendicular bearing, radians
        const groundPt    = groundPoint( sat.rec, now, sat.orientation );
        const dLat = ( HALF_SWATH_KM / KM_PER_DEG ) * Math.cos( crossTrack );
        const dLon = ( HALF_SWATH_KM / KM_PER_DEG ) * Math.sin( crossTrack )
            / Math.cos( sat.position[1] * Math.PI / 180 );            
        return {
            from: [groundPt[0] - dLon, groundPt[1] - dLat, 1000],
            to:   [groundPt[0] + dLon, groundPt[1] + dLat, 1000],
        };
    } ), [now] );
    
    const dataLayers = useMemo( () => [
        new ArcLayer<Swath>( {
            id: "swath",
            data: swaths,
            getSourcePosition: d => d.from,
            getTargetPosition: d => d.to,
            getSourceColor: [255, 0, 200, 220],
            getTargetColor: [255, 0, 200, 220],
            getWidth: 3,
            greatCircle: true,
            getHeight: 0,
        } ),
        target && new SimpleMeshLayer( {
            id:             "target-marker",
            data:           [0],
            mesh:           cubeMesh,
            getPosition:    [ target[0], target[1], 0 ],
            getOrientation: [   0, 180,  0 ],
            getColor:       [ 255,   0,  0 ],
            getScale:       [20000, 20000, 20000],
            pickable:       true
        } ),
        target && new TextLayer( {
            id:                     "target-label",
            data:                   [0],
            getPosition:            [target[0], target[1], 50000],
            getText:                d => "Target",
            getSize:                14,
            getColor:               [0, 0, 0],
            getTextAnchor:          "middle",
            getAlignmentBaseline:   "bottom",
            background:             true,
            getAngle:               -180,
            backgroundBorderRadius: 4,
            getBackgroundColor:     [255, 255, 255, 255],
            backgroundPadding:      [4, 4, 4, 4],
            billboard:              false,
        } ),
        new ScenegraphLayer<Sat>( {
            id:             "satellite",
            data:           sats,
            scenegraph:     "satv20.gltf",
            getPosition:    ( d: Sat ) => [d.position[0], d.position[1], d.position[2] * orbitScale],
            getOrientation: d => d.orientation,
            _lighting:      "pbr",
            getTranslation: [0, 0, 10000],
            getScale:       [250000, 250000, 250000],
            pickable:       true,
        } ),
        new ArcLayer<ArcData> ( {
            id: "trail",
            data: sats.map( sat => {
                const t1   = new Date( time );
                const t2   = new Date( time - ( 60000 * 10 ) );
                const pos1 = sgp4ToLonLatAlt( sat.rec, t1 );
                const pos2 = sgp4ToLonLatAlt( sat.rec, t2 );
                return {
                    to:   [pos1[0], pos1[1], pos1[2] * orbitScale],
                    from: [pos2[0], pos2[1], pos2[2] * orbitScale],
                };
            } ),
            getSourcePosition: d => d.from,
            getTargetPosition: d => d.to,
            getSourceColor:    [0, 255, 255,   0],
            getTargetColor:    [0, 255, 255, 255],
            getWidth:          2,
            greatCircle:       true,
            getHeight:         0
        } ),
        new TextLayer<Sat>( {
            id:                     "sat-labels",
            data:                   sats,
            getPosition:            d => [d.position[0], d.position[1], ( ( d.position[2] + orbitTextOffset ) * orbitTextScale ) ],
            getText:                d => d.name,
            getSize:                12,
            getColor:               [0, 0, 0],
            getTextAnchor:          "middle",
            getAlignmentBaseline:   "center",
            billboard:              true,  // Makes text always face the camera
            background:             true,  // Adds a background box
            getBackgroundColor:     [255, 255, 255, 128],  // Semi-transparent black background
            backgroundPadding:      [7, 6, 7, 5],  // Padding around text
            backgroundBorderRadius: 4, 
        } ),
    ], [target, now, cubeMesh] );

    // console.log( `Perf: ${(( performance.now() - perfStart ) ).toFixed(1)} ms` );
    let sunPos: number | undefined = undefined;
    if( target ) {
        sunPos = toDeg( SunCalc.getPosition( now, target[1], target[0] ).altitude );
    }
    
    return (
        <div className="h-100">
            <div id="capture-list-page">
                <div id="capture-list" className="bg-body-tertiary border border-end-0">
                    <div className="border-bottom p-2 d-flex flex-column">                        
                        <Toolbar 
                            now={now}
                            sunPosition={sunPos}                            
                            mode={mode}
                            chooseDate={ () => {
                                const res = prompt( "Enter Date: YYYY-MM-DD" );
                                if( res !== undefined && res !== null && res !== "" ) {
                                    setAnchor( Date.parse( res ) );
                                    setStart( Date.now() );
                                }
                            }}
                            rewind={ () => {
                                console.log( now.toISOString() );
                                setMode( SimMode.Rewind );
                                setAnchor( time );
                                setStart( Date.now() );
                            } }
                            play={ () => {
                                console.log( now.toISOString() );
                                setMode( SimMode.Play );
                                setAnchor( time );
                                setStart( Date.now() );
                            } }
                            pause={ () => {
                                console.log( now.toISOString() );                                
                                setMode( SimMode.Pause );
                                setAnchor( time );
                                setStart( Date.now() );
                            } }
                            onLocationFound={ ( lat, lng, name ) => {
                                setTarget( [lng, lat] );
                                setViewState( { ...viewState, latitude: lat, longitude: lng } );                                
                            } }
                            rate={rate}
                            turbo={ () => {
                                setAnchor( now.getTime() );
                                setStart( Date.now() );
                                if( rate ===    1 ) { setRate(   60 ); return; }
                                if( rate ===   60 ) { setRate(  600 ); return; }
                                if( rate ===  600 ) { setRate(  900 ); return; }
                                if( rate ===  900 ) { setRate( 3600 ); return; }
                                if( rate === 3600 ) { setRate(    1 ); return; }
                                return;
                            }} />
                    </div>
                    <div className="y-scroll-auto flex-grow-1">
                        {sats.map( ( { id, name, rec, epoch } ) => {
                            return <MemoSatelliteEntry key={id} satId={id} satName={name} rec={rec} target={target} now={now} epoch={epoch} />;
                        } ) }
                    </div>
                </div>
                <div className="image-viewer h-100 overflow-hidden position-relative"
                    style={{ backgroundColor: "black" }}>
                    <DeckGL
                        views={globeView}
                        initialViewState={viewState}
                        controller={{ inertia: false }}
                        effects={[lights]}
                        layers={[bgLayers, dataLayers]}
                        style={{ width: "100%", height: "100vh", background: "#000000" }}
                    />
                </div>
            </div>
        </div>
    );
}